import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { slugify } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = ({ businessName, onBusinessName, dark = false, idPrefix = "lead" }) => {
    const [contact, setContact] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(null);
    const slug = slugify(businessName || "");

    const submit = async (e) => {
        e.preventDefault();
        if (!slug) {
            toast.error("Enter your business name first.");
            return;
        }
        if (contact.trim().length < 5) {
            toast.error("Add your email or WhatsApp number.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/leads`, { business_name: businessName.trim(), contact: contact.trim() });
            setDone(data);
            toast.success("You’re on the waitlist.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div className="rounded-[2rem] border-2 border-ink bg-paper p-8 text-center sm:p-10" data-testid={`${idPrefix}-success`}>
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-ink">
                    <CheckCircle2 className="h-7 w-7 text-white" />
                </span>
                <p className="mt-5 font-display text-2xl font-bold text-ink">You’re on the list.</p>
                <p className="mt-3 text-base text-neutral-600">
                    Your proposed address: <span className="font-bold text-ink">{done.preview_url}</span>
                </p>
                <p className="mt-2 text-sm text-neutral-500">
                    We’ll reach you on your {done.contact_type === "whatsapp" ? "WhatsApp number" : "email"} with next steps and plan details.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="space-y-4" data-testid={`${idPrefix}-form`}>
            <div>
                <label htmlFor={`${idPrefix}-business`} className={`text-xs font-bold uppercase tracking-[0.18em] ${dark ? "text-white/50" : "text-neutral-400"}`}>
                    Your business name
                </label>
                <div className={`mt-2 flex items-center rounded-2xl border-2 transition-colors ${dark ? "border-white/20 bg-white/5 focus-within:border-white/50" : "border-ink bg-white focus-within:border-neutral-500"}`}>
                    <span className={`whitespace-nowrap pl-4 text-sm font-bold sm:text-base ${dark ? "text-white/40" : "text-neutral-400"}`}>
                        purplebusiness.in/
                    </span>
                    <input
                        id={`${idPrefix}-business`}
                        data-testid={`${idPrefix}-business-input`}
                        value={businessName}
                        onChange={(e) => onBusinessName(e.target.value)}
                        placeholder="your-business"
                        maxLength={60}
                        className={`w-full min-w-0 bg-transparent px-2 py-4 font-bold outline-none ${dark ? "text-white placeholder:text-white/30" : "text-ink placeholder:text-neutral-300"}`}
                    />
                </div>
            </div>
            <div>
                <label htmlFor={`${idPrefix}-contact`} className={`text-xs font-bold uppercase tracking-[0.18em] ${dark ? "text-white/50" : "text-neutral-400"}`}>
                    Email or WhatsApp number
                </label>
                <input
                    id={`${idPrefix}-contact`}
                    data-testid={`${idPrefix}-contact-input`}
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    placeholder="you@example.com or +91 98765 43210"
                    maxLength={120}
                    className={`mt-2 w-full rounded-2xl border-2 px-4 py-4 font-bold outline-none transition-colors ${dark ? "border-white/20 bg-white/5 text-white placeholder:text-white/30 focus:border-white/50" : "border-ink bg-white text-ink placeholder:text-neutral-300 focus:border-neutral-500"}`}
                />
            </div>
            <button
                type="submit"
                data-testid={`${idPrefix}-submit-button`}
                disabled={loading}
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 text-base font-bold text-white transition-colors duration-300 hover:bg-neutral-700 disabled:opacity-60"
            >
                {loading ? "Joining…" : "Start my Purple Business"}
                {!loading && <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />}
            </button>
            <p className={`text-center text-xs ${dark ? "text-white/40" : "text-neutral-400"}`}>
                We’ll only use this to share your preview and waitlist updates.
            </p>
        </form>
    );
};
